
import { useEffect, useRef } from 'react';

const ABANDON_SENT_KEY = 'cart_abandon_sent_date';

interface UseCartAbandonParams {
  cartCount: number;
  subtotal: number;
}

/**
 * Registra un evento de carrito abandonado (best-effort, máx 1 vez por sesión de página)
 * cuando el usuario oculta o cierra la pestaña con items en el carrito.
 * Usa navigator.sendBeacon para máxima fiabilidad al cerrar/ocultar pestaña.
 */
export function useCartAbandon({ cartCount, subtotal }: UseCartAbandonParams) {
  const cartCountRef = useRef(cartCount);
  const subtotalRef = useRef(subtotal);
  const abandonLogged = useRef(false);

  // Mantener refs actualizados sin re‑bindear listeners
  useEffect(() => {
    cartCountRef.current = cartCount;
    subtotalRef.current = subtotal;
  }, [cartCount, subtotal]);

  useEffect(() => {
    const supabaseUrl =
      import.meta.env.VITE_PUBLIC_SUPABASE_URL ||
      import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey =
      import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY ||
      import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseKey) {
      return;
    }

    /** Comprueba si ya se envió el evento en el día actual */
    const alreadySentToday = (): boolean => {
      try {
        const last = localStorage.getItem(ABANDON_SENT_KEY);
        if (!last) return false;
        // Comparamos sólo la parte de la fecha (YYYY‑MM‑DD)
        return last === new Date().toISOString().slice(0, 10);
      } catch {
        return false;
      }
    };

    /** Marca en localStorage que el evento fue enviado hoy */
    const markSent = () => {
      try {
        localStorage.setItem(
          ABANDON_SENT_KEY,
          new Date().toISOString().slice(0, 10)
        );
      } catch {
        // Ignoramos errores de almacenamiento
      }
    };

    /** Obtiene el user‑id guardado por Supabase (si existe) */
    const getUserId = (): string | null => {
      try {
        const host = (supabaseUrl as string)
          .replace(/^https?:\/\//, '')
          .split('.')[0];
        const raw = localStorage.getItem(`sb-${host}-auth-token`);
        if (raw) {
          const parsed = JSON.parse(raw);
          return parsed?.user?.id ?? null;
        }
      } catch {
        // Ignoramos errores de parsing
      }
      return null;
    };

    /** Envía el evento de abandono de carrito */
    const logAbandon = () => {
      // Guardas: carrito vacío, ya registrado en esta sesión, ya enviado hoy
      if (cartCountRef.current === 0) return;
      if (abandonLogged.current) return;
      if (alreadySentToday()) return;

      const sessionId = localStorage.getItem('cart_session_id');
      if (!sessionId) return;

      const userId = getUserId();

      // Marcar inmediatamente para evitar duplicados
      abandonLogged.current = true;
      markSent();

      const rpcUrl = `${supabaseUrl}/rest/v1/rpc/log_cart_abandon`;
      const body = JSON.stringify({
        p_session_id: sessionId,
        p_user_id: userId,
        p_item_count: cartCountRef.current,
        p_subtotal: subtotalRef.current,
      });

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        apikey: supabaseKey as string,
        Authorization: `Bearer ${supabaseKey}`,
      };

      // Intentar sendBeacon primero (más fiable al cerrar pestaña)
      if (navigator.sendBeacon) {
        try {
          const blob = new Blob([body], { type: 'application/json' });
          const sent = navigator.sendBeacon(`${rpcUrl}?apikey=${supabaseKey}`, blob);
          if (sent) return;
        } catch {
          // Si falla, caemos al fallback
        }
      }

      // Fallback: fetch con keepalive
      try {
        fetch(rpcUrl, {
          method: 'POST',
          headers,
          body,
          keepalive: true,
        }).catch(() => {
          // Silencioso: no bloquear la navegación
        });
      } catch {
        // Silencioso
      }
    };

    /** Cuando la página pasa a hidden se registra el abandono */
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        logAbandon();
      }
    };

    /** Cuando la ventana está a punto de descargarse */
    const handleBeforeUnload = () => {
      logAbandon();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup listeners al desmontar
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []); // Ejecutar una sola vez al montar
}
